<script setup lang="ts">
import { useFormStore } from '~/store/form.store';

const formStore = useFormStore();

const user = reactive({
  name: '',
  email: '',
  speciality: '',
  comment: '',
});

const hasInteractedWithName = ref(false);
const hasInteractedWithEmail = ref(false);
const hasInteractedWithSpeciality = ref(false);
const hasInteractedWithComment = ref(false);
const userCheckBox = ref<boolean>(true);
const hasInteractedWithCheckbox = ref(false);
const showSnackbarWarning = ref(false);

const isFormSending = ref(false);
const isFormSuccess = ref(false);

const submitForm = async () => {
  if (isSubmitFormDisabled.value) return;
  isFormSending.value = true;

  // Simulate form submission
  const res = await formStore.requestForm({
    name: user.name,
    email: user.email,
    specialty: user.speciality,
    comment: user.comment,
  });

  isFormSending.value = false;
  // Reset form fields
  if (res) {
    resetForm();
    isFormSuccess.value = true;
    setTimeout(() => {
      isFormSuccess.value = false;
    }, 5000);
  } else {
    // Show snackbar warning if submission fails
    showSnackbarWarning.value = true;
  }
};

const resetForm = () => {
  user.name = '';
  user.email = '';
  user.speciality = '';
  user.comment = '';
  hasInteractedWithName.value = false;
  hasInteractedWithEmail.value = false;
  hasInteractedWithSpeciality.value = false;
  hasInteractedWithComment.value = false;
  userCheckBox.value = true;
  hasInteractedWithCheckbox.value = false;
};

const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

const isEmailValid = computed(() => {
  return emailPattern.test(user.email);
});

const isSubmitFormDisabled = computed(() => {
  return (
    !isEmailValid.value ||
    !user.name ||
    !user.speciality ||
    !user.comment ||
    !userCheckBox.value
  );
});
</script>

<template>
  <section id="join" class="join-wrapper">
    <div class="join">
      <nuxt-img src="/v2/team-decorator.png" class="join__decorator" alt="" />
      <div class="title-wrapper">
        <h2 class="title headline-2">
          Think <span class="title--white middle-word">we’re</span
          ><br class="new-line" /><span class="title--white title--second">
            a match?</span
          >
        </h2>
        <p class="font-title title--white subtitle">
          Fill in the form and let’s go!
        </p>
      </div>

      <form class="form" @submit.prevent="submitForm">
        <div
          class="input__box"
          :class="{ error: hasInteractedWithName && !user.name }"
        >
          <div
            class="input__border"
            :class="{ 'input__border--active': user.name }"
          >
            <label for="userName" class="input__label font-subtitle"
              >Name</label
            >
            <input
              id="userName"
              v-model="user.name"
              class="input font-subtitle"
              type="text"
              placeholder="Name"
              name="userName"
              autocomplete="name"
              required
              maxlength="256"
              @input="hasInteractedWithName = true"
            />
          </div>
          <Transition name="fade">
            <p
              v-if="hasInteractedWithName && !user.name"
              class="input__error font-bodytext"
            >
              Name required. Please enter your name.
            </p>
          </Transition>
        </div>

        <div
          class="input__box"
          :class="{ error: hasInteractedWithEmail && !user.email }"
        >
          <div
            class="input__border"
            :class="{ 'input__border--active': user.email }"
          >
            <label for="userEmail" class="input__label font-subtitle"
              >Email</label
            >
            <input
              id="userEmail"
              v-model="user.email"
              class="input font-subtitle"
              type="email"
              placeholder="Email"
              name="userEmail"
              autocomplete="email"
              maxlength="256"
              required
              @input="hasInteractedWithEmail = true"
            />
          </div>
          <Transition name="fade">
            <p
              v-if="hasInteractedWithEmail && !user.email"
              class="input__error font-bodytext"
            >
              Email required. Please enter your email.
            </p>
            <p
              v-else-if="hasInteractedWithEmail && !isEmailValid"
              class="input__error font-bodytext"
            >
              Invalid email format. Please enter a valid email.
            </p>
          </Transition>
        </div>

        <div
          class="input__box"
          :class="{ error: hasInteractedWithSpeciality && !user.speciality }"
        >
          <div
            class="input__border"
            :class="{ 'input__border--active': user.speciality }"
          >
            <label for="userSpeciality" class="input__label font-subtitle"
              >Speciality</label
            >
            <input
              id="userSpeciality"
              v-model="user.speciality"
              class="input font-subtitle"
              type="text"
              placeholder="Speciality"
              name="userSpeciality"
              autocomplete="speciality"
              maxlength="256"
              required
              @input="hasInteractedWithSpeciality = true"
            />
          </div>
          <Transition name="fade">
            <p
              v-if="hasInteractedWithSpeciality && !user.speciality"
              class="input__error font-bodytext"
            >
              Speciality required. Please enter your speciality.
            </p>
          </Transition>
        </div>

        <div
          class="input__box"
          :class="{ error: hasInteractedWithComment && !user.comment }"
        >
          <div
            class="input__border textarea-border"
            :class="{ 'input__border--active': user.comment }"
          >
            <label
              for="userMessage"
              class="input__label font-subtitle input__label--textarea"
              >Tell us about yourself</label
            >
            <textarea
              id="userMessage"
              v-model="user.comment"
              class="input__textarea input font-subtitle"
              placeholder="tell us about yourself"
              name="userMessage"
              autocomplete="comment"
              required
              maxlength="2000"
              rows="5"
              @input="hasInteractedWithComment = true"
            />
          </div>
          <Transition name="fade">
            <p
              v-if="hasInteractedWithComment && !user.comment"
              class="input__error font-bodytext"
            >
              Comment required. Please enter your comment.
            </p>
          </Transition>
        </div>

        <div class="checkbox-box">
          <div class="checkbox-wrapper">
            <base-checkbox
              v-model="userCheckBox"
              required
              @input="hasInteractedWithCheckbox = true"
            />
            <p class="font-bodytext">
              I agree with the
              <nuxt-link
                class="checkbox-link font-bodytext"
                to="/policy"
                target="_blank"
                >privacy policy</nuxt-link
              >.
            </p>
          </div>
          <Transition name="fade">
            <p
              v-if="hasInteractedWithCheckbox && !userCheckBox"
              class="input__error font-bodytext"
            >
              Please accept the privacy policy to continue
            </p>
          </Transition>
        </div>

        <button
          ref="submitBtn"
          type="submit"
          class="form__button"
          :disabled="isFormSending"
        >
          <transition name="fade" mode="out-in">
            <div v-if="isFormSending" class="loader">
              <div class="ball"></div>
              <div class="ball"></div>
              <div class="ball"></div>
            </div>
            <p v-else-if="isFormSuccess" class="font-button-text button__text">
              <icon size="20px" name="icons:check-line" />
              Sent
            </p>
            <p v-else class="font-button-text button__text">Send</p></transition
          >
        </button>
      </form>
    </div>
    <base-snackbar v-model="showSnackbarWarning" variant="warning">
      <template #title>Oops! Something went wrong.</template>
      <template #text>{{ formStore.errorMessage }}</template>
    </base-snackbar>
  </section>
</template>

<style scoped>
.join-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.join {
  position: relative;
  width: 100%;
  max-width: 1440px;
  padding: 100px 40px 120px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.join__decorator {
  position: absolute;
  top: -22px;
  left: 40px;
  width: 100%;
  max-width: 600px;
  height: auto;
}

.title-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  z-index: 1;
}

.title {
  width: 100%;
  text-align: left;
  text-transform: uppercase;
}

.title--white {
  color: var(--color-white);
}

.title--second {
  padding-left: 117px;
}

.subtitle {
  padding: 20px 0 0 117px;
}

.form {
  width: 100%;
  max-width: 660px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.input__box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.input {
  width: 100%;
  padding: 0 10px;
  height: 42px;
  color: var(--black);
  border-radius: 4px;
  border: 1px solid transparent;
  background-color: var(--white);
  outline: none;
}

.input::placeholder {
  color: var(--white);
}

.input__border {
  position: relative;
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--grey-gradient);
  padding: 1px;
}

.input__border:hover,
.input__border:focus-within {
  background: var(--logo-gradient);
}

.input__label {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 12px;
  opacity: 0.4;
  color: var(--black);
  text-transform: uppercase;
  pointer-events: none;
  background-color: var(--white);
  transition: all 0.2s;
}

.input__label--textarea {
  top: 12px;
  left: 12px;
  transform: translateY(0);
}

.input__border:focus-within .input__label,
.input__border.input__border--active .input__label {
  padding: 0 4px;
  top: 0;
  left: 12px;
  font-size: 12px;
  opacity: 1;
  color: var(--color-grey-gradient);
  border-radius: 4px;
  transform: translateY(-50%) scale(0.9);
}

.input__textarea {
  resize: none;
  height: 162px;
  padding-top: 10px;
}

.textarea-border {
  height: 164px;
}

.input__error {
  color: var(--danger);
  margin: 4px 0 0 0;
}

.error .input__border {
  background: var(--danger);
}

.error .input__label {
  color: var(--danger) !important;
  opacity: 1 !important;
}

.form__button {
  border-radius: 4px;
  background-color: var(--main-blue);
  border: none;
  outline: none;
  height: 44px;
  padding: 12px 60px;
  color: var(--color-white);
  cursor: pointer;
  align-self: center;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: background-color 0.3s ease, box-shadow 0.3s ease,
    transform 0.3s ease;
}

.form__button:hover {
  box-shadow: 8px 4px 40px 0 var(--main-blue);
}

.form__button:active {
  box-shadow: 8px 4px 40px 0 var(--main-blue);
  transform: scale(0.97);
}

/* .form__button:disabled {
  opacity: 0.32;
  background-color: var(--black);
  cursor: not-allowed;
} */

.button__text {
  color: var(--color-white);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loader {
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: 0.5s linear;
}

.ball {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: bounce6135 1s alternate infinite;
  transition: 0.5s linear;
}

.ball {
  background: var(--white);
}

.ball:nth-child(2) {
  animation-delay: 0.25s;
}

.ball:nth-child(3) {
  animation-delay: 0.5s;
}

.checkbox-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.checkbox-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
}

.checkbox-link {
  color: var(--main-blue);
  cursor: pointer;
  text-decoration: none;
}

@keyframes bounce6135 {
  from {
    transform: scale(2);
  }

  to {
    transform: scale(1);
  }
}

@media (max-width: 1200px) {
  .join {
    padding: 100px 20px 120px;
  }

  .join__decorator {
    top: -28px;
    left: 20px;
  }

  .form {
    max-width: 415px;
  }
}

@media (max-width: 1023px) {
  .join {
    flex-direction: column;
    padding: 41px 20px 60px;
  }

  .join__decorator {
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
  }

  .title-wrapper {
    align-items: center;
  }

  .title {
    text-align: center;
    max-width: 380px;
    margin-bottom: 32px;
  }

  .title--second {
    padding-left: 0;
  }

  .new-line {
    display: none;
  }

  .middle-word {
    color: var(--black);
  }

  .subtitle {
    padding: 0;
    margin-bottom: 20px;
  }

  .form {
    max-width: 100%;
  }

  .form__button {
    width: 100%;
  }
}

@media (max-width: 743px) {
  .join__decorator {
    width: 600px;
  }

  .new-line {
    display: unset;
  }

  .subtitle {
    margin-bottom: 18px;
  }
}
</style>
